import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import FirearmOwnerTable from '../../../components/tables/FirearmOwnerTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useFirearmOwnerSearch, useDeleteFirearmOwner } from '../../../hooks/useFirearmOwnerApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { FirearmOwnerDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';

const FirearmOwnerList: React.FC = () => {
  const { search } = useFirearmOwnerSearch();
  const deleteFirearmOwner = useDeleteFirearmOwner();
  const [items, setItems] = useState<FirearmOwnerDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Firearm Owners', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    setIsLoading(true);
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
      return response.totalCount;
    } catch (error) {
      console.error('Error fetching FirearmOwners:', error);
      return 0;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const handleDelete = async (dto: FirearmOwnerDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteFirearmOwner.mutateAsync(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting Firearm Owner:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/firearmowners"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.FirearmOwner.View"
    >
      <Card
        title="Manage Firearm Owners"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/firearmowners/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
          </div>
          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <FirearmOwnerTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/firearmowners/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default FirearmOwnerList;
